import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import { API_BASE_URL } from "../constant/api";
import { apiRequest, uploadFile } from "../utils/http";
import { namedColors } from "../utils/colors";

const defaultBranding = {
  siteName: "Gentle Renters",
  logoUrl: "",
  logoPublicId: "",
  topBarColor: "#f59e0b",
  addToCartColor: "#f59e0b",
  footerBackgroundColor: "#000000",
  brochureUrl: "",
  brochurePublicId: "",
  facebookUrl: "",
  instagramUrl: "",
  twitterUrl: "",
  tiktokUrl: "",
  faqs: [
    {
      question: "How do I request a quotation?",
      answer: "Browse listings, add your preferred items to cart, and generate a quotation with your event date and location."
    },
    {
      question: "Do you deliver event rentals?",
      answer: "Yes. Delivery and logistics can be arranged based on your event location and booking details."
    }
  ],
  privacyPolicyContent:
    "We respect your privacy and only use your personal details to process quotations, bookings, customer support, and service communication.",
  cancellationPolicyContent:
    "Cancellations should be communicated early so we can review timing, logistics, and any non-refundable arrangements already committed to your booking.",
  brochureContent:
    "Our brochure highlights featured rental collections, event styling inspiration, and booking guidance for your celebration.",
  sliderImages: [],
  sliderSpeed: 3000
};

const SettingsPage = () => {
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [brandingForm, setBrandingForm] = useState(defaultBranding);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingBranding, setIsSavingBranding] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingSlider, setIsUploadingSlider] = useState(false);
  const [isUploadingBrochure, setIsUploadingBrochure] = useState(false);

  useEffect(() => {
    apiRequest("/api/site-settings")
      .then((response) => setBrandingForm({ ...defaultBranding, ...(response?.data || {}) }))
      .catch((loadError) => setError(loadError.message || "Unable to load site settings."));
  }, []);

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswordForm((current) => ({ ...current, [name]: value }));
  };

  const handleBrandingChange = (event) => {
    const { name, value } = event.target;
    setBrandingForm((current) => ({ ...current, [name]: value }));
  };

  const handleColorPick = (field, color) => {
    setBrandingForm((current) => ({
      ...current,
      [field]: color?.value || current[field]
    }));
  };

  const handleFaqChange = (index, field, value) => {
    setBrandingForm((current) => ({
      ...current,
      faqs: current.faqs.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleAddFaq = () => {
    setBrandingForm((current) => ({
      ...current,
      faqs: [...(current.faqs || []), { question: "", answer: "" }]
    }));
  };

  const handleRemoveFaq = (index) => {
    setBrandingForm((current) => ({
      ...current,
      faqs: current.faqs.filter((_, itemIndex) => itemIndex !== index)
    }));
  };

  const handleSubmitPassword = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError("New password and confirmation do not match.");
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await apiRequest("/api/auth/change-password", {
        method: "PATCH",
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });
      setSuccess(response?.message || "Password changed successfully.");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (changeError) {
      setError(changeError.message || "Unable to change password.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setError("");
    setSuccess("");

    try {
      setIsUploadingLogo(true);
      const response = await uploadFile("/api/uploads/image", file, { folder: "gentle-renters/site" });
      setBrandingForm((current) => ({
        ...current,
        logoUrl: response?.data?.imageUrl || "",
        logoPublicId: response?.data?.imagePublicId || ""
      }));
      setSuccess("Logo uploaded successfully.");
    } catch (uploadError) {
      setError(uploadError.message || "Unable to upload logo.");
    } finally {
      setIsUploadingLogo(false);
      event.target.value = "";
    }
  };

  const handleSliderUpload = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setError("");
    setSuccess("");

    try {
      setIsUploadingSlider(true);
      const response = await uploadFile("/api/uploads/image", file, { folder: "gentle-renters/site" });
      setBrandingForm((current) => ({
        ...current,
        sliderImages: [...(current.sliderImages || []), response?.data?.imageUrl].filter(Boolean)
      }));
      setSuccess("Slider image uploaded successfully.");
    } catch (uploadError) {
      setError(uploadError.message || "Unable to upload slider image.");
    } finally {
      setIsUploadingSlider(false);
      event.target.value = "";
    }
  };

  const handleBrochureUpload = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setError("");
    setSuccess("");

    try {
      setIsUploadingBrochure(true);
      const response = await uploadFile(
        "/api/uploads/document",
        file,
        {
          folder: "gentle-renters/brochures",
          publicId: brandingForm.brochurePublicId || ""
        },
        "document"
      );

      setBrandingForm((current) => ({
        ...current,
        brochureUrl: response?.data?.fileUrl || current.brochureUrl,
        brochurePublicId: response?.data?.filePublicId || current.brochurePublicId
      }));
      setSuccess("Brochure uploaded successfully.");
    } catch (uploadError) {
      setError(uploadError.message || "Unable to upload brochure.");
    } finally {
      setIsUploadingBrochure(false);
      event.target.value = "";
    }
  };

  const handleRemoveSliderImage = (imageIndex) => {
    setBrandingForm((current) => ({
      ...current,
      sliderImages: current.sliderImages.filter((_, index) => index !== imageIndex)
    }));
  };

  const handleSaveBranding = async () => {
    setError("");
    setSuccess("");

    try {
      setIsSavingBranding(true);
      const response = await apiRequest("/api/site-settings", {
        method: "PUT",
        body: JSON.stringify({
          ...brandingForm,
          sliderSpeed: Number(brandingForm.sliderSpeed)
        })
      });
      setBrandingForm({ ...defaultBranding, ...(response?.data || {}) });
      setSuccess(response?.message || "Site settings updated successfully.");
    } catch (saveError) {
      setError(saveError.message || "Unable to save site settings.");
    } finally {
      setIsSavingBranding(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" sx={{ mb: 1 }}>Settings</Typography>
        <Typography sx={{ color: "text.secondary" }}>
          Manage your account security and storefront branding from one place.
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1.5 }}>Connected API</Typography>
              <Typography sx={{ color: "text.secondary", wordBreak: "break-word" }}>{API_BASE_URL}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1.5 }}>Change Your Password</Typography>
              <Box component="form" onSubmit={handleSubmitPassword}>
                <Stack spacing={2}>
                  <TextField label="Current Password" name="currentPassword" type="password" value={passwordForm.currentPassword} onChange={handlePasswordChange} fullWidth />
                  <TextField label="New Password" name="newPassword" type="password" value={passwordForm.newPassword} onChange={handlePasswordChange} fullWidth />
                  <TextField label="Confirm New Password" name="confirmPassword" type="password" value={passwordForm.confirmPassword} onChange={handlePasswordChange} fullWidth />
                  <Button type="submit" variant="contained" disabled={isSubmitting}>
                    {isSubmitting ? "Updating..." : "Update Password"}
                  </Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h5" sx={{ mb: 1 }}>Storefront Branding</Typography>
              <Typography sx={{ color: "text.secondary" }}>
                Super admins can update the public logo, brochure file, top bar color, listing button color, and home slider settings.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <Stack spacing={2.5}>
                  <TextField label="Site Name" name="siteName" value={brandingForm.siteName} onChange={handleBrandingChange} fullWidth />
                  <TextField
                    label="Brochure Download URL"
                    name="brochureUrl"
                    value={brandingForm.brochureUrl}
                    onChange={handleBrandingChange}
                    placeholder="https://..."
                    fullWidth
                  />
                  <TextField
                    label="Facebook URL"
                    name="facebookUrl"
                    value={brandingForm.facebookUrl}
                    onChange={handleBrandingChange}
                    placeholder="https://facebook.com/yourpage"
                    fullWidth
                  />
                  <TextField
                    label="Instagram URL"
                    name="instagramUrl"
                    value={brandingForm.instagramUrl}
                    onChange={handleBrandingChange}
                    placeholder="https://instagram.com/yourhandle"
                    fullWidth
                  />
                  <TextField
                    label="Twitter/X URL"
                    name="twitterUrl"
                    value={brandingForm.twitterUrl}
                    onChange={handleBrandingChange}
                    placeholder="https://x.com/yourhandle"
                    fullWidth
                  />
                  <TextField
                    label="TikTok URL"
                    name="tiktokUrl"
                    value={brandingForm.tiktokUrl}
                    onChange={handleBrandingChange}
                    placeholder="https://tiktok.com/@yourhandle"
                    fullWidth
                  />

                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Top Bar Color</Typography>
                    <Autocomplete
                      options={namedColors}
                      getOptionLabel={(option) => `${option.name} (${option.value})`}
                      value={namedColors.find((item) => item.value.toLowerCase() === String(brandingForm.topBarColor).toLowerCase()) || null}
                      onChange={(_, value) => handleColorPick("topBarColor", value)}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Box sx={{ width: 18, height: 18, borderRadius: "50%", backgroundColor: option.value, border: "1px solid rgba(0,0,0,0.2)" }} />
                          <span>{option.name}</span>
                          <Typography sx={{ color: "text.secondary" }}>{option.value}</Typography>
                        </Box>
                      )}
                      renderInput={(params) => <TextField {...params} placeholder="Search colors" fullWidth />}
                    />
                    <Typography sx={{ color: "text.secondary", mt: 1 }}>{brandingForm.topBarColor}</Typography>
                  </Box>

                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Listing Buttons Color</Typography>
                    <Autocomplete
                      options={namedColors}
                      getOptionLabel={(option) => `${option.name} (${option.value})`}
                      value={namedColors.find((item) => item.value.toLowerCase() === String(brandingForm.addToCartColor).toLowerCase()) || null}
                      onChange={(_, value) => handleColorPick("addToCartColor", value)}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Box sx={{ width: 18, height: 18, borderRadius: "50%", backgroundColor: option.value, border: "1px solid rgba(0,0,0,0.2)" }} />
                          <span>{option.name}</span>
                          <Typography sx={{ color: "text.secondary" }}>{option.value}</Typography>
                        </Box>
                      )}
                      renderInput={(params) => <TextField {...params} placeholder="Search colors" fullWidth />}
                    />
                    <Typography sx={{ color: "text.secondary", mt: 1 }}>
                      Used for both Add to Cart and View Listing buttons.
                    </Typography>
                    <Typography sx={{ color: "text.secondary", mt: 1 }}>{brandingForm.addToCartColor}</Typography>
                  </Box>

                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Footer Background Color</Typography>
                    <Autocomplete
                      options={namedColors}
                      getOptionLabel={(option) => `${option.name} (${option.value})`}
                      value={namedColors.find((item) => item.value.toLowerCase() === String(brandingForm.footerBackgroundColor).toLowerCase()) || null}
                      onChange={(_, value) => handleColorPick("footerBackgroundColor", value)}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Box sx={{ width: 18, height: 18, borderRadius: "50%", backgroundColor: option.value, border: "1px solid rgba(0,0,0,0.2)" }} />
                          <span>{option.name}</span>
                          <Typography sx={{ color: "text.secondary" }}>{option.value}</Typography>
                        </Box>
                      )}
                      renderInput={(params) => <TextField {...params} placeholder="Search colors" fullWidth />}
                    />
                    <Typography sx={{ color: "text.secondary", mt: 1 }}>{brandingForm.footerBackgroundColor}</Typography>
                  </Box>

                  <TextField
                    label="Slider Speed (ms)"
                    name="sliderSpeed"
                    type="number"
                    value={brandingForm.sliderSpeed}
                    onChange={handleBrandingChange}
                    fullWidth
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} lg={6}>
                <Stack spacing={2.5}>
                  <Button component="label" variant="outlined" startIcon={<CloudUploadRoundedIcon />} disabled={isUploadingLogo}>
                    {isUploadingLogo ? "Uploading Logo..." : "Upload Site Logo"}
                    <input hidden type="file" accept="image/*" onChange={handleLogoUpload} />
                  </Button>

                  {brandingForm.logoUrl && (
                    <Box component="img" src={brandingForm.logoUrl} alt="Site logo" sx={{ width: 180, height: "auto", borderRadius: 2 }} />
                  )}

                  <Button component="label" variant="outlined" startIcon={<CloudUploadRoundedIcon />} disabled={isUploadingSlider}>
                    {isUploadingSlider ? "Uploading Slider Image..." : "Upload Slider Image"}
                    <input hidden type="file" accept="image/*" onChange={handleSliderUpload} />
                  </Button>

                  <Button component="label" variant="outlined" startIcon={<CloudUploadRoundedIcon />} disabled={isUploadingBrochure}>
                    {isUploadingBrochure ? "Uploading Brochure..." : "Upload Brochure PDF"}
                    <input hidden type="file" accept="application/pdf" onChange={handleBrochureUpload} />
                  </Button>

                  {brandingForm.brochureUrl && (
                    <Typography sx={{ color: "text.secondary", wordBreak: "break-word" }}>
                      Current brochure: {brandingForm.brochureUrl}
                    </Typography>
                  )}
                </Stack>
              </Grid>
            </Grid>

            <Box>
              <Typography variant="h6" sx={{ mb: 1.5 }}>Slider Images</Typography>
              <Grid container spacing={2}>
                {(brandingForm.sliderImages || []).map((image, index) => (
                  <Grid item xs={12} sm={6} md={4} key={`${image}-${index}`}>
                    <Box sx={{ position: "relative" }}>
                      <Box component="img" src={image} alt={`Slider ${index + 1}`} sx={{ width: "100%", height: 180, objectFit: "cover", borderRadius: 2 }} />
                      <IconButton
                        onClick={() => handleRemoveSliderImage(index)}
                        sx={{ position: "absolute", top: 8, right: 8, backgroundColor: "rgba(255,255,255,0.9)" }}
                      >
                        <DeleteOutlineRoundedIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Button variant="contained" onClick={handleSaveBranding} disabled={isSavingBranding} sx={{ width: { xs: "100%", md: "fit-content" } }}>
              {isSavingBranding ? "Saving..." : "Save Site Settings"}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h5" sx={{ mb: 1 }}>Footer Pages Content</Typography>
              <Typography sx={{ color: "text.secondary" }}>
                Update the content used on the footer links for FAQs, privacy policy, cancellation policy, and brochure page.
              </Typography>
            </Box>

            <Stack spacing={2}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Typography variant="h6">FAQs</Typography>
                <Button variant="outlined" onClick={handleAddFaq}>Add FAQ</Button>
              </Box>

              {(brandingForm.faqs || []).map((faq, index) => (
                <Card key={`faq-${index}`} variant="outlined">
                  <CardContent>
                    <Stack spacing={2}>
                      <TextField
                        label={`Question ${index + 1}`}
                        value={faq.question}
                        onChange={(event) => handleFaqChange(index, "question", event.target.value)}
                        fullWidth
                      />
                      <TextField
                        label={`Answer ${index + 1}`}
                        value={faq.answer}
                        onChange={(event) => handleFaqChange(index, "answer", event.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                      />
                      <Box>
                        <Button color="error" onClick={() => handleRemoveFaq(index)}>
                          Remove FAQ
                        </Button>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>

            <TextField
              label="Privacy Policy Content"
              name="privacyPolicyContent"
              value={brandingForm.privacyPolicyContent}
              onChange={handleBrandingChange}
              fullWidth
              multiline
              minRows={6}
            />

            <TextField
              label="Cancellation Policy Content"
              name="cancellationPolicyContent"
              value={brandingForm.cancellationPolicyContent}
              onChange={handleBrandingChange}
              fullWidth
              multiline
              minRows={6}
            />

            <TextField
              label="Brochure Page Content"
              name="brochureContent"
              value={brandingForm.brochureContent}
              onChange={handleBrandingChange}
              fullWidth
              multiline
              minRows={6}
            />

            <Button variant="contained" onClick={handleSaveBranding} disabled={isSavingBranding} sx={{ width: { xs: "100%", md: "fit-content" } }}>
              {isSavingBranding ? "Saving..." : "Save Footer Pages"}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default SettingsPage;
